import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Loader2, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HOME } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/hooks/useBilling";
import { toast } from "sonner";

const STARTER_FEATURES = [
  "10 AI messages / month",
  "AI Reply Assistant",
  "Project memory + file uploads",
  "Single workspace",
];

const PRO_FEATURES = [
  "Unlimited AI messages",
  "Proposal Generator (Pro tool)",
  "Client Analyzer (Pro tool)",
  "Priority streaming + early features",
  "Cancel anytime",
];

function formatPrice(currency, amount) {
  if (currency === "USD") return `$${amount.toFixed(2)}`;
  return `₹${Math.round(amount)}`;
}

export function PricingTeaser() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { pricing, billing, currency, setCurrency, isPro, upgrade } = useBilling();
  const [cycle, setCycle] = useState("monthly"); // 'monthly' | 'yearly'
  const [busy, setBusy] = useState(false);

  // Pricing for the currently selected currency/cycle
  let proPriceLabel = "—";
  let proSubLabel = "";
  let savingsLabel = null;
  if (pricing?.pro?.[currency]) {
    const monthly = pricing.pro[currency].monthly;
    const yearly = pricing.pro[currency].yearly;
    const monthlyAmount = currency === "USD" ? monthly.amount_cents / 100 : monthly.amount_paise / 100;
    const yearlyAmount  = currency === "USD" ? yearly.amount_cents  / 100 : yearly.amount_paise  / 100;

    if (cycle === "monthly") {
      proPriceLabel = formatPrice(currency, monthlyAmount);
      proSubLabel = "/month";
    } else {
      // Show yearly total + per-month equivalent
      const perMonth = yearlyAmount / 12;
      proPriceLabel = formatPrice(currency, perMonth);
      proSubLabel = "/month, billed yearly";
      const savings = (monthlyAmount * 12) - yearlyAmount;
      if (savings > 0) {
        savingsLabel = `Save ${formatPrice(currency, savings)} / year (20% off)`;
      }
    }
  }

  const onUpgrade = async () => {
    if (!user) {
      navigate(`/signup?next=/pricing&plan=pro&cycle=${cycle}`);
      return;
    }
    if (isPro) {
      toast.info("You're already on Pro 🎉");
      return;
    }
    setBusy(true);
    try {
      await upgrade(cycle);
      toast.success("Welcome to Pro! Unlimited messages unlocked.");
      navigate("/workspace");
    } catch (e) {
      if (e.message !== "Checkout closed") {
        toast.error(e.message || "Couldn't complete payment");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid={HOME.pricingSection}
      className="mx-auto w-full max-w-5xl px-5 py-24 sm:px-8 sm:py-32"
    >
      <div className="mx-auto max-w-2xl text-center">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
          Pricing
        </p>
        <h2 className="mt-3 text-balance text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          Start free. Upgrade when MACHA pays for itself.
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Two plans. No surprises. Cancel any time — your data leaves with you.
        </p>

        {/* Currency + Cycle toggles */}
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-5">
          <div className="inline-flex rounded-xl border border-border bg-card p-1" role="tablist" aria-label="Currency">
            {["USD", "INR"].map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={currency === c}
                data-testid={`pricing-currency-${c.toLowerCase()}`}
                onClick={() => setCurrency(c)}
                className={[
                  "rounded-lg px-3 py-1.5 text-[12px] font-medium transition-colors",
                  currency === c
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                ].join(" ")}
              >
                {c === "USD" ? "USD ($)" : "INR (₹)"}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-xl border border-border bg-card p-1" role="tablist" aria-label="Billing cycle">
            {["monthly", "yearly"].map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={cycle === c}
                data-testid={`pricing-cycle-${c}`}
                onClick={() => setCycle(c)}
                className={[
                  "rounded-lg px-3 py-1.5 text-[12px] font-medium capitalize transition-colors",
                  cycle === c
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                ].join(" ")}
              >
                {c}
                {c === "yearly" && (
                  <span className="ml-1.5 rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-500">
                    -20%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Starter */}
        <div
          data-testid={HOME.pricingCard("starter")}
          className="relative flex flex-col rounded-2xl border border-border bg-card p-7"
        >
          <h3 className="text-[15px] font-semibold tracking-tight text-foreground">Starter</h3>
          <p className="mt-1.5 text-[13px] text-muted-foreground">
            Try the copilot on a few proposals each month.
          </p>
          <div className="mt-6 flex items-baseline gap-1">
            <span className="font-display text-4xl font-semibold tracking-tight text-foreground">Free</span>
            <span className="text-sm text-muted-foreground">forever</span>
          </div>
          <ul className="mt-6 space-y-2.5">
            {STARTER_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-[13.5px] text-foreground/90">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <div className="mt-7 pt-1">
            <Button
              asChild
              variant="outline"
              className="h-10 w-full rounded-xl border-border bg-background text-[13px] font-medium text-foreground hover:bg-secondary"
            >
              <Link to={user ? "/workspace" : "/signup"} data-testid={HOME.pricingCta("starter")}>
                {billing?.plan === "starter" || !user ? "Start free" : "Current plan"}
              </Link>
            </Button>
          </div>
        </div>

        {/* Pro */}
        <div
          data-testid={HOME.pricingCard("pro")}
          className="relative flex flex-col rounded-2xl border border-primary/50 bg-card p-7 shadow-[0_0_0_1px_hsl(var(--primary)/0.25)]"
        >
          <span className="absolute -top-2.5 right-6 rounded-full border border-primary/40 bg-primary/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
            Most popular
          </span>
          <h3 className="text-[15px] font-semibold tracking-tight text-foreground">Pro</h3>
          <p className="mt-1.5 text-[13px] text-muted-foreground">
            For freelancers replying to clients daily.
          </p>
          <div className="mt-6 flex items-baseline gap-1">
            <span
              data-testid="pricing-pro-price"
              className="font-display text-4xl font-semibold tracking-tight text-foreground"
            >
              {proPriceLabel}
            </span>
            <span className="text-sm text-muted-foreground">{proSubLabel}</span>
          </div>
          {savingsLabel && (
            <p className="mt-1.5 text-[12px] font-medium text-emerald-500" data-testid="pricing-pro-savings">
              {savingsLabel}
            </p>
          )}
          <ul className="mt-6 space-y-2.5">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-[13.5px] text-foreground/90">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <div className="mt-7 pt-1">
            <Button
              type="button"
              onClick={onUpgrade}
              disabled={busy || isPro}
              data-testid={HOME.pricingCta("pro")}
              className="h-10 w-full gap-1.5 rounded-xl bg-primary text-[13px] font-medium text-primary-foreground hover:bg-primary/90"
            >
              {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Opening checkout…</>
                : isPro ? <>Current plan</>
                : <><Crown className="h-3.5 w-3.5" /> Get Pro</>}
            </Button>
            {!user && (
              <p className="mt-2.5 text-center text-[11.5px] text-muted-foreground">
                Sign in to upgrade — your trial usage carries over.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
